/**
 * What the eligibility form says a lead wants, in the intro-call card's vocabulary.
 *
 * The form is the first place a lead names their procedure, goal and prior surgery,
 * and until now nothing read it: `readEligibilityPrefill` was a hard-coded `{}`, so
 * the card's three prefilled fields started empty and the Intake and billing
 * panel's Service row sat blank for every lead without a *saved* intro-call note.
 *
 * Two things make this a translation rather than a read. The answers are nested
 * inside group items (`interest-eligibility`), so a flat scan finds nothing. And
 * they are codes from the questionnaire's own value sets — `bypass`, `regain` —
 * which are not the card's option labels. The maps below are transcribed from
 * `packages/bots/data/questionnaire/eligibility-v21.json`; a code the questionnaire
 * does not define is ignored rather than guessed at.
 */

import { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';

export const ELIGIBILITY_QUESTIONNAIRE_URL = 'http://bariendo.com/fhir/Questionnaire/eligibility-v21';

const PROCEDURE_LINK_ID = 'interested-in';
const GOAL_LINK_ID = 'goal';
const PRIOR_LINK_ID = 'prior-procedure';
const CONDITIONS_LINK_ID = 'conditions';
const MARKET_LINK_ID = 'nearest-location';
const DISTANCE_LINK_ID = 'distance-mi';
const PAYMENT_MODE_LINK_ID = 'payment-mode';
const EMPLOYER_LINK_ID = 'employer';
const EMPLOYER_COVERED_LINK_ID = 'employer-covered';

/**
 * `interested-in` shares its codes with the card's `PROCEDURE_OPTIONS`, so most of
 * this is identity. Two do not map:
 *
 * - `ReESG` is a real offering the card's select predates. Surfaced as-is, because
 *   the panel row deliberately shows a stored value outside its option list rather
 *   than dropping it — hiding it would make a real choice look like missing data.
 * - `Custom` names nothing in particular, so there is nothing to prefill.
 */
const PROCEDURE_BY_CODE: Record<string, string | undefined> = {
  ESG: 'ESG',
  // Not on the eligibility form yet; identity so it passes through the day it is.
  'ESG Max': 'ESG Max',
  IGB: 'IGB',
  SIS: 'SIS',
  TORe: 'TORe',
  ReESG: 'ReESG',
  Custom: undefined,
};

/**
 * The questionnaire offers a `balloon` prior that the card's select has no option
 * for. It folds into "another procedure" rather than being dropped: the rep still
 * sees that there *was* a prior procedure, and the beat's script has them confirm
 * it out loud regardless.
 */
const PRIOR_BY_CODE: Record<string, string> = {
  none: 'None',
  sleeve: 'Gastric sleeve',
  bypass: 'Gastric bypass',
  balloon: 'Another procedure, including LAP-Band',
  esg_other: 'ESG with another provider',
  other: 'Another procedure, including LAP-Band',
};

/** Questionnaire displays are shorter than the card's; matched by code, not text. */
const GOAL_BY_CODE: Record<string, string> = {
  lasting: 'Find a lasting way to lose weight',
  health: 'Improve my health condition',
  alt_glp: 'Find an alternative to GLP-1',
  learn_esg: 'Learn more about ESG',
  regain: 'Lose weight I’ve regained after bariatric surgery',
  explore: 'Just explore my options',
};

function findItem(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string
): QuestionnaireResponseItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId === linkId) return item;

    const nested = findItem(item.item, linkId);
    if (nested) return nested;

    for (const answer of item.answer ?? []) {
      const withinAnswer = findItem(answer.item, linkId);
      if (withinAnswer) return withinAnswer;
    }
  }

  return undefined;
}

/** The answer's code, tolerating a plain string for a form that sends one. */
function readCode(response: QuestionnaireResponse | undefined, linkId: string): string | undefined {
  const answer = findItem(response?.item, linkId)?.answer?.[0];
  const code = answer?.valueCoding?.code ?? answer?.valueString;

  return code && code.length > 0 ? code : undefined;
}

/** The `distance-mi` answer, which the form stores as a decimal, not a code. */
function readDecimal(response: QuestionnaireResponse | undefined, linkId: string): number | undefined {
  const value = findItem(response?.item, linkId)?.answer?.[0]?.valueDecimal;

  return typeof value === 'number' ? value : undefined;
}

export interface EligibilityIntakeSelections {
  /** Card `procedure` — also the Service row on the Intake and billing panel. */
  procedure?: string;
  /** Card `wn_main`, the goal at signup. */
  goal?: string;
  /** Card `prior`, the previous bariatric surgery. */
  prior?: string;
  /**
   * Card `loc` — also the Location row on the Intake and billing panel.
   *
   * Not translated, unlike `procedure`: since BAR-2597 this is already a canonical
   * market name from the Medplum registry, and a map here would reintroduce exactly
   * the two-vocabulary drift that ticket removed.
   */
  market?: string;
  /**
   * Straight-line miles from the lead's ZIP to that market.
   *
   * Shown beside the market on the panel because bariendo-be no longer applies a
   * radius cutoff — an out-of-market lead now records a real market, and the
   * distance is what tells a rep that "Boston" means 340 miles away.
   */
  distanceMi?: number;
}

export function readEligibilityIntakeSelections(
  response: QuestionnaireResponse | undefined
): EligibilityIntakeSelections {
  const procedureCode = readCode(response, PROCEDURE_LINK_ID);
  const goalCode = readCode(response, GOAL_LINK_ID);
  const priorCode = readCode(response, PRIOR_LINK_ID);

  const procedure = procedureCode ? PROCEDURE_BY_CODE[procedureCode] : undefined;
  const goal = goalCode ? GOAL_BY_CODE[goalCode] : undefined;
  const prior = priorCode ? PRIOR_BY_CODE[priorCode] : undefined;
  const market = readCode(response, MARKET_LINK_ID);
  const distanceMi = readDecimal(response, DISTANCE_LINK_ID);

  return {
    ...(procedure ? { procedure } : {}),
    ...(goal ? { goal } : {}),
    ...(prior ? { prior } : {}),
    ...(market ? { market } : {}),
    // Compared against null, not falsy: zero miles is a real answer for a lead
    // inside a market, and `?` would silently drop it.
    ...(distanceMi != null ? { distanceMi } : {}),
  };
}

/**
 * Every code ticked on signup's "Do any of these apply?" (`conditions`, one
 * answer per box). Raw codes: the PN card's vocabulary and the map onto it live
 * in `weight-related-conditions.ts`, because this module must stay free of
 * relative imports for `deploy/bots-registry.ts` to load it under Node's ESM
 * loader (BAR-2794).
 */
export function readEligibilityConditionCodes(response: QuestionnaireResponse | undefined): string[] {
  return (findItem(response?.item, CONDITIONS_LINK_ID)?.answer ?? [])
    .map((answer) => answer.valueCoding?.code ?? answer.valueString)
    .filter((code): code is string => Boolean(code));
}

/**
 * `payment-mode` codes in the intake card's `PAYMENT_OPTIONS` vocabulary, so the
 * patient list renders a self-reported preference and a rep-confirmed answer
 * with the same words. `employer` on the form is "Check employer benefit" — a
 * request to check, which the card records as the benefit itself once confirmed.
 *
 * Two vocabularies, deliberately. `eligibility-v21.json` defines `monthly` and
 * `unsure`, but the live flow (bariendo-be `eligibility-steps.config.ts`) writes
 * `financing` and `undecided` — measured on staging as 48 and 4 responses against
 * 2 and 1 for the questionnaire's own codes. Both spellings map; a code neither
 * defines is ignored rather than guessed at.
 */
const PAYMENT_BY_CODE: Record<string, string> = {
  financing: 'Monthly financing',
  monthly: 'Monthly financing',
  full: 'Pay in full',
  hsa: 'HSA or FSA',
  employer: 'Employer benefit',
  // BAR-2902: the payment page's "Insurance or employer benefit" option. The card
  // has no matching `PAYMENT_OPTIONS` entry yet, so this one is the form's label.
  coverage: 'Insurance or employer benefit',
  undecided: 'Not sure yet',
  unsure: 'Not sure yet',
};

export interface EligibilityPayment {
  /** Card `pay` vocabulary. */
  paymentMethod?: string;
  /** Free text the lead typed; absent unless they chose the employer route. */
  employer?: string;
  /** The form's own match against the covered-employer list. */
  employerCovered?: boolean;
}

/** The Payment group of the eligibility form. */
export function readEligibilityPayment(response: QuestionnaireResponse | undefined): EligibilityPayment {
  const modeCode = readCode(response, PAYMENT_MODE_LINK_ID);
  const paymentMethod = modeCode ? PAYMENT_BY_CODE[modeCode] : undefined;
  const employer = readCode(response, EMPLOYER_LINK_ID);
  const employerCovered = findItem(response?.item, EMPLOYER_COVERED_LINK_ID)?.answer?.[0]?.valueBoolean;

  return {
    ...(paymentMethod ? { paymentMethod } : {}),
    ...(employer ? { employer } : {}),
    ...(employerCovered !== undefined ? { employerCovered } : {}),
  };
}

/**
 * The form's coverage answers (BAR-2919), as the labels it stores:
 * `coverage-source` ("Through a job", "I bought it myself", …) and `insurer`
 * (the payer's name). The PN card's insurance section prefills from them when
 * the signup Coverage lacks them.
 */
export function readEligibilityCoverage(response: QuestionnaireResponse | undefined): {
  source?: string;
  insurer?: string;
} {
  const source = readCode(response, 'coverage-source');
  const insurer = readCode(response, 'insurer');
  return { ...(source ? { source } : {}), ...(insurer ? { insurer } : {}) };
}
